/*
 * hook: useArchidektLink
 *
 * Generates a link to the archidekt tester, which requires card metadata
 *
 */
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

import { type UtmType } from '@/defs/misc'
import { type RecType } from '@/defs/rec'
import { useCardMap } from '@/hooks/useCardMap'
import { encodeArchidektDeck } from '@/utils/brandUtil'
import { getUtmLink, isNonEmptyString } from '@/utils/commonUtil'

const DECK_URL = 'https://archidekt.com/sandbox'
const PLAYTESTER_URL = 'https://archidekt.com/playtester-v2/sandbox'

type Props = {
  link: 'playtester' | 'deck'
  recs: RecType[]
  utm?: UtmType
}

export const useArchidektLink = ({ link, recs, utm }: Props) => {
  const baseUrl = link === 'playtester' ? PLAYTESTER_URL : DECK_URL
  const cardIds = useMemo(
    () => recs.flatMap(({ cardId, partnerRef }) => [cardId, partnerRef?.cardId]).filter(isNonEmptyString),
    [recs],
  )
  const [enabled, setEnabled] = useState(false)
  const [pending, setPending] = useState(false)
  const windowTabRef = useRef<Window | null>(null)
  const { cardMap, hasError, loading } = useCardMap({ cardIds, enabled })
  const isReady = enabled && !loading
  const href = getUtmLink(
    isReady ? `${baseUrl}?deck=${encodeURI(JSON.stringify(encodeArchidektDeck({ cardMap, recs })))}` : baseUrl,
    utm,
  )

  useEffect(() => {
    if (!pending) return
    if (!isReady && !hasError) return
    const windowTab = windowTabRef.current
    windowTabRef.current = null
    if (hasError) windowTab?.close()
    else if (windowTab && !windowTab.closed) windowTab.location.replace(href)
    else window.open(href, '_blank', 'noopener,noreferrer')
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPending(false)
  }, [hasError, href, pending, isReady])

  const onClick = (e: MouseEvent) => {
    if (isReady) return

    e.preventDefault()
    if (pending) {
      windowTabRef.current?.focus()
      return
    }

    const windowTab = window.open('', '_blank')
    if (windowTab) windowTab.opener = null
    windowTabRef.current = windowTab
    setEnabled(true)
    setPending(true)
  }

  const onInteract = () => setEnabled(true)

  return { href, loading: loading && pending, onClick, onInteract }
}
